import { ProjectCard, ProjectCardData } from '../../landing/components/ProjectCard';
import { useTranslation } from '../../../core/hooks/useTranslation';
import { Link } from 'react-router-dom';

interface UnitsGridProps {
    units: any[];
    title?: string;
    viewAllLink?: string;
    /** Project name — used as the location label on each card */
    projectName?: string;
    /** Project-level delivery year — fallback when per-unit deliveryDate is null */
    deliveryDate?: string;
    /** Project-level finish type — fallback when per-unit finishType is null */
    projectFinish?: string;
}

/**
 * Maps a raw unit object (from project.units) to the standardized ProjectCardData structure.
 */
function mapUnitToCardData(
    unit: any,
    t: any,
    projectName?: string,
    fallbackDeliveryDate?: string,
    fallbackFinish?: string
): ProjectCardData {
    return {
        id: unit.id,
        title: unit.title || unit.slug || '',
        location: unit.project?.title || projectName || '',
        specs: {
            beds: unit.beds ?? 0,
            baths: unit.baths ?? 0,
            area: `${unit.area ?? 0} ${t('common.m2', 'm²')}`
        },
        price: unit.price,
        payment: unit.payment || t('common.installments', 'Installments'),
        status: unit.status || '',
        image: unit.image || '',
        constructionStatus: unit.constructionStatus || undefined,
        finishType: unit.finishType || fallbackFinish || undefined,
        deliveryDate: unit.deliveryDate || fallbackDeliveryDate || undefined,
    };
}

export const UnitsGrid = ({ units, title, viewAllLink, projectName, deliveryDate, projectFinish }: UnitsGridProps) => {
    const { t } = useTranslation();

    if (!units || units.length === 0) return null;

    return (
        <section>
            {/* Header */}
            {(title || viewAllLink) && (
                <div className="flex items-center justify-between mb-6">
                    {title && (
                        <h3 className="text-2xl font-display font-bold text-secondary flex items-center gap-3">
                            <span className="w-8 h-[2px] bg-secondary block" />
                            {title}
                        </h3>
                    )}
                    {viewAllLink && (
                        <Link
                            to={viewAllLink}
                            className="text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                        >
                            {t('common.viewAll', 'View All')} →
                        </Link>
                    )}
                </div>
            )}

            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {units.map((unit) => {
                    const projectData = mapUnitToCardData(unit, t, projectName, deliveryDate, projectFinish);

                    return (
                        <div key={unit.id}>
                            {/* Mobile: Horizontal Layout */}
                            <div className="block md:hidden">
                                <ProjectCard
                                    project={projectData}
                                    linkTo={`/units/${unit.slug || unit.id}`}
                                    layout="horizontal-mobile"
                                    type="Unit"
                                />
                            </div>
                            {/* Desktop: Standard (Vertical) Layout */}
                            <div className="hidden md:block">
                                <ProjectCard
                                    project={projectData}
                                    linkTo={`/units/${unit.slug || unit.id}`}
                                    layout="standard"
                                    type="Unit"
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
};
